import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldAlert, ArrowLeft, Home } from 'lucide-react';
import { getPermissionDescription } from '@/lib/utils/permission-descriptions';

interface LocationState {
    requiredPermission?: string;
    attemptedPath?: string;
}

export default function ForbiddenPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as LocationState;

    const requiredPermission = state?.requiredPermission || 'Unknown permission';
    const attemptedPath = state?.attemptedPath || 'this page';
    const permissionDescription = getPermissionDescription(requiredPermission);

    return (
        <div className="flex items-center justify-center min-h-screen bg-background p-4">
            <Card className="max-w-md w-full">
                <CardHeader className="text-center">
                    <div className="flex justify-center mb-4">
                        <div className="rounded-full bg-destructive/10 p-3">
                            <ShieldAlert className="h-12 w-12 text-destructive" />
                        </div>
                    </div>
                    <CardTitle className="text-2xl">Access Denied</CardTitle>
                    <CardDescription>
                        You don't have permission to access this resource
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="bg-muted p-4 rounded-lg space-y-3">
                        <div>
                            <p className="text-sm font-medium mb-1">Required Permission:</p>
                            <p className="text-sm text-foreground">
                                {permissionDescription}
                            </p>
                        </div>
                        <div className="pt-2 border-t">
                            <p className="text-xs text-muted-foreground mb-1">Technical ID:</p>
                            <code className="text-xs bg-background px-2 py-1 rounded border">
                                {requiredPermission}
                            </code>
                        </div>
                    </div>

                    <p className="text-sm text-muted-foreground">
                        If you believe you should have access to this page, please contact your
                        administrator to request the necessary permissions.
                    </p>

                    <div className="flex gap-2 pt-4">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Go Back
                        </Button>
                        <Button
                            className="flex-1"
                            onClick={() => navigate('/')}
                        >
                            <Home className="h-4 w-4 mr-2" />
                            Dashboard
                        </Button>
                    </div>

                    {process.env.NODE_ENV === 'development' && (
                        <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded text-xs">
                            <p className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                                Debug Info (Development Only):
                            </p>
                            <p className="text-yellow-700 dark:text-yellow-300">
                                Attempted path: <code>{attemptedPath}</code>
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
